import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
  Table,
} from "reactstrap";
import cogoToast from "cogo-toast";
import Swal from "sweetalert2";
import { FaEdit, FaEye, FaPlus, FaSearch, FaSync, FaTrash } from "react-icons/fa";
import {
  deleteWorkforcePlanningApi,
  listWorkforcePlanningApi,
  updateWorkforcePlanningStatusApi,
} from "../../../services/Api";
import { ADMIN_ROLE, SUCCESS_RESULT } from "../../../Constants";
import ability from "../../config/defineAbility";
import { PERMS } from "../../config/perms";
import { getEncryptedUserData } from "../../../services/Utils";
import {
  COMMITTEE_DECISION_OPTIONS,
  CRITICALITY_OPTIONS,
  criticalityColor,
  decisionColor,
  formatCell,
  formatDate,
  formatMoney,
  isWorkforcePlanningCompleted,
  recordStatusColor,
  recordStatusLabel,
  WORKFORCE_STATUS_OPTIONS,
} from "./workforcePlanningHelpers";
import "./WorkforcePlanningList.css";

const EMPTY_FILTERS = {
  office: "",
  area: "",
  criticality_category: "",
  committee_decision: "",
  role_id: "",
  project_name: "",
};

const WorkforcePlanningList = () => {
  const history = useHistory();
  const canDelete = ability.can(PERMS.DELETE, PERMS.CAN_DELETE_WORKFORCE_PLANNING);
  const canCreate = ability.can(PERMS.CREATE, PERMS.CAN_CREATE_WORKFORCE_PLANNING);
  const isSuperAdmin = (() => {
    try {
      const user = getEncryptedUserData();
      if (!user) return false;
      const userRole = user.role || user.role_id;
      return parseInt(userRole, 10) === ADMIN_ROLE || userRole === ADMIN_ROLE;
    } catch {
      return false;
    }
  })();
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState({ ...EMPTY_FILTERS });
  const [appliedFilters, setAppliedFilters] = useState({ ...EMPTY_FILTERS });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [instructionsOpen, setInstructionsOpen] = useState(false);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [statusTarget, setStatusTarget] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [savingStatus, setSavingStatus] = useState(false);

  useEffect(() => {
    fetchList(appliedFilters);
  }, [appliedFilters]);

  const openCreateInstructions = () => setInstructionsOpen(true);

  const closeCreateInstructions = () => setInstructionsOpen(false);

  const confirmCreateInstructions = () => {
    setInstructionsOpen(false);
    history.push("/hrm/workforce-planning/create");
  };

  const fetchList = (filterPayload = {}) => {
    setLoading(true);
    const body = {};
    Object.entries(filterPayload).forEach(([key, value]) => {
      if (value !== null && value !== undefined && String(value).trim() !== "") {
        body[key] = String(value).trim();
      }
    });

    listWorkforcePlanningApi(body)
      .then((res) => {
        setLoading(false);
        if (res.status === 200 && res.data.status === SUCCESS_RESULT) {
          const data = res.data.data;
          setRows(Array.isArray(data) ? data : []);
        } else {
          setRows([]);
          cogoToast.error(res.data?.message || "Failed to load records", {
            hideAfter: 5,
          });
        }
      })
      .catch((err) => {
        setLoading(false);
        setRows([]);
        cogoToast.error(
          err?.response?.data?.message || "Something went wrong loading records.",
          { hideAfter: 5 }
        );
      });
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const applyFilters = (e) => {
    e?.preventDefault?.();
    setAppliedFilters({ ...filters });
  };

  const clearFilters = () => {
    setFilters({ ...EMPTY_FILTERS });
    setAppliedFilters({ ...EMPTY_FILTERS });
    setSearch("");
  };

  const handleDelete = (row) => {
    if (!canDelete) {
      cogoToast.error("You do not have permission to delete workforce planning records.", {
        hideAfter: 4,
      });
      return;
    }
    Swal.fire({
      title: "Delete this record?",
      text: `${row.role?.role_name || "Role"} · ${row.office || "—"} will be removed.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Yes, delete",
    }).then((result) => {
      if (!result.isConfirmed) return;
      deleteWorkforcePlanningApi({ id: row.id })
        .then((res) => {
          if (res.status === 200 && res.data.status === SUCCESS_RESULT) {
            cogoToast.success(res.data.message || "Deleted", { hideAfter: 3 });
            fetchList(appliedFilters);
          } else {
            cogoToast.error(res.data?.message || "Delete failed", { hideAfter: 5 });
          }
        })
        .catch((err) => {
          cogoToast.error(
            err?.response?.data?.message || "Something went wrong deleting.",
            { hideAfter: 5 }
          );
        });
    });
  };

  const openStatusModal = (row) => {
    if (!isSuperAdmin) return;
    setStatusTarget(row);
    setSelectedStatus(
      row?.status === null || row?.status === undefined || row?.status === ""
        ? ""
        : String(row.status)
    );
    setStatusModalOpen(true);
  };

  const closeStatusModal = () => {
    if (savingStatus) return;
    setStatusModalOpen(false);
    setStatusTarget(null);
    setSelectedStatus("");
  };

  const saveStatus = () => {
    if (!isSuperAdmin) {
      cogoToast.error("Only super admin can update status.", { hideAfter: 4 });
      return;
    }
    if (!statusTarget?.id) {
      cogoToast.error("Record id is missing.", { hideAfter: 4 });
      return;
    }
    if (!selectedStatus) {
      cogoToast.error("Please select a status.", { hideAfter: 4 });
      return;
    }

    setSavingStatus(true);
    updateWorkforcePlanningStatusApi({
      id: statusTarget.id,
      status: Number(selectedStatus),
    })
      .then((res) => {
        setSavingStatus(false);
        if (res.status === 200 && res.data.status === SUCCESS_RESULT) {
          cogoToast.success(res.data.message || "Status updated", { hideAfter: 3 });
          setStatusModalOpen(false);
          setStatusTarget(null);
          setSelectedStatus("");
          fetchList(appliedFilters);
        } else {
          cogoToast.error(res.data?.message || "Failed to update status", {
            hideAfter: 5,
          });
        }
      })
      .catch((err) => {
        setSavingStatus(false);
        cogoToast.error(
          err?.response?.data?.message || "Something went wrong updating status.",
          { hideAfter: 5 }
        );
      });
  };

  const visibleRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) => {
      const haystack = [
        row.role?.role_name,
        row.office,
        row.area,
        row.project_name,
        row.criticality_category,
        row.committee_decision,
        row.budget_line,
        row.comments,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [rows, search]);

  return (
    <div className="container-fluid px-3 px-lg-4 mt-4 mb-5 wfp-list">
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <div>
          <h2 className="page-title mb-1">Workforce Planning</h2>
          <div className="text-muted">HRM · Plan staffing needs, gaps and funding</div>
        </div>
        <div className="d-flex gap-2">
          <Button color="secondary" outline onClick={() => fetchList(appliedFilters)}>
            <FaSync className="me-1" /> Refresh
          </Button>
        </div>
      </div>

      <Card className="shadow-sm mb-3">
        <CardBody>
          <form onSubmit={applyFilters}>
            <Row className="g-2 align-items-end">
              <Col md={2}>
                <FormGroup className="mb-2">
                  <Label>Office</Label>
                  <Input
                    name="office"
                    value={filters.office}
                    onChange={handleFilterChange}
                    placeholder="Office"
                  />
                </FormGroup>
              </Col>
              <Col md={2}>
                <FormGroup className="mb-2">
                  <Label>Area</Label>
                  <Input
                    name="area"
                    value={filters.area}
                    onChange={handleFilterChange}
                    placeholder="Area"
                  />
                </FormGroup>
              </Col>
              <Col md={2}>
                <FormGroup className="mb-2">
                  <Label>Criticality</Label>
                  <Input
                    type="select"
                    name="criticality_category"
                    value={filters.criticality_category}
                    onChange={handleFilterChange}
                  >
                    <option value="">All</option>
                    {CRITICALITY_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>
              <Col md={2}>
                <FormGroup className="mb-2">
                  <Label>Committee decision</Label>
                  <Input
                    type="select"
                    name="committee_decision"
                    value={filters.committee_decision}
                    onChange={handleFilterChange}
                  >
                    <option value="">All</option>
                    {COMMITTEE_DECISION_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>
              <Col md={2}>
                <FormGroup className="mb-2">
                  <Label>Project</Label>
                  <Input
                    name="project_name"
                    value={filters.project_name}
                    onChange={handleFilterChange}
                    placeholder="Project name"
                  />
                </FormGroup>
              </Col>
              <Col md={2}>
                <FormGroup className="mb-2">
                  <Label>Role ID</Label>
                  <Input
                    name="role_id"
                    value={filters.role_id}
                    onChange={handleFilterChange}
                    placeholder="role UUID"
                  />
                </FormGroup>
              </Col>
              <Col md={12} className="d-flex gap-2 flex-wrap align-items-center">
                <Button color="primary" type="submit">
                  <FaSearch className="me-1" /> Apply filters
                </Button>
                <Button color="secondary" outline type="button" onClick={clearFilters}>
                  Clear
                </Button>
                {canCreate && (
                  <Button
                    color="success"
                    type="button"
                    className="ms-auto"
                    onClick={openCreateInstructions}
                  >
                    <FaPlus className="me-1" /> New record
                  </Button>
                )}
              </Col>
            </Row>
          </form>
        </CardBody>
      </Card>

      <Card className="shadow-sm">
        <CardBody>
          <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
            <div className="text-muted">
              {loading ? "Loading…" : `${visibleRows.length} record(s)`}
            </div>
            <Input
              style={{ maxWidth: 280 }}
              placeholder="Quick search…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          {loading ? (
            <div className="text-center py-5">
              <Spinner color="primary" />
            </div>
          ) : visibleRows.length === 0 ? (
            <div className="text-center text-muted py-5">No workforce planning records found.</div>
          ) : (
            <div className="table-responsive wfp-list-table-wrap">
              <Table hover className="align-middle mb-0 wfp-list-table">
                <thead>
                  <tr>
                    <th>Role</th>
                    <th>Office</th>
                    <th>Area</th>
                    <th>Status</th>
                    <th>Staff</th>
                    <th>Gaps</th>
                    <th>Budget</th>
                    <th>Months</th>
                    <th>Total salary</th>
                    <th>Score</th>
                    <th>Criticality</th>
                    <th>Decision</th>
                    <th>Updated</th>
                    <th style={{ width: 140 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.map((row) => (
                    <tr key={row.id}>
                      <td>
                        <div className="fw-semibold">{formatCell(row.role?.role_name)}</div>
                        <small className="text-muted">{formatCell(row.project_name)}</small>
                      </td>
                      <td>{formatCell(row.office)}</td>
                      <td>{formatCell(row.area)}</td>
                      <td>
                        <div className="d-flex align-items-center gap-1">
                          <Badge color={recordStatusColor(row.status)}>
                            {recordStatusLabel(row.status)}
                          </Badge>
                          {isSuperAdmin && (
                            <Button
                              size="sm"
                              color="warning"
                              outline
                              className="p-1"
                              title="Update status"
                              onClick={() => openStatusModal(row)}
                            >
                              <FaEdit />
                            </Button>
                          )}
                        </div>
                      </td>
                      <td>
                        <div>Current: {formatCell(row.current_staff_count)}</div>
                        <small className="text-muted">
                          Min {formatCell(row.minimum_staff_needed)} · Stable{" "}
                          {formatCell(row.stable_staff_needed)} · Growth{" "}
                          {formatCell(row.growth_staff_needed)}
                        </small>
                      </td>
                      <td>
                        <small>
                          Min {formatCell(row.minimum_gap_surplus)}
                          <br />
                          Stable {formatCell(row.stable_gap_surplus)}
                          <br />
                          Growth {formatCell(row.growth_gap_surplus)}
                        </small>
                      </td>
                      <td>
                        <div>{formatMoney(row.available_budget)}</div>
                        <small className="text-muted">
                          Monthly {formatMoney(row.monthly_gross_salary)}
                          <br />
                          Gap {formatMoney(row.budget_surplus_gap)}
                        </small>
                      </td>
                      <td>{formatCell(row.nbr_of_months)}</td>
                      <td>{formatMoney(row.total_salary)}</td>
                      <td>{formatCell(row.total_score)}</td>
                      <td>
                        <Badge color={criticalityColor(row.criticality_category)}>
                          {formatCell(row.criticality_category)}
                        </Badge>
                      </td>
                      <td>
                        <Badge color={decisionColor(row.committee_decision)}>
                          {formatCell(row.committee_decision)}
                        </Badge>
                      </td>
                      <td>
                        <small>{formatDate(row.updated_at || row.created_at)}</small>
                      </td>
                      <td>
                        <div className="d-flex gap-1">
                          <Button
                            size="sm"
                            color="info"
                            outline
                            title="View"
                            onClick={() =>
                              history.push(`/hrm/workforce-planning/details/${row.id}`)
                            }
                          >
                            <FaEye />
                          </Button>
                          {!isWorkforcePlanningCompleted(row.status) && (
                            <Button
                              size="sm"
                              color="primary"
                              outline
                              title="Edit"
                              onClick={() =>
                                history.push(`/hrm/workforce-planning/edit/${row.id}`)
                              }
                            >
                              <FaEdit />
                            </Button>
                          )}
                          {canDelete && (
                            <Button
                              size="sm"
                              color="danger"
                              outline
                              title="Delete"
                              onClick={() => handleDelete(row)}
                            >
                              <FaTrash />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </CardBody>
      </Card>

      <Modal
        isOpen={instructionsOpen}
        toggle={closeCreateInstructions}
        size="lg"
        centered
        className="wfp-instructions-modal"
      >
        <ModalHeader toggle={closeCreateInstructions}>
          Workforce Planning Guidelines
        </ModalHeader>
        <ModalBody className="wfp-instructions-body">
          <div className="wfp-instructions-section">
            <div className="wfp-instructions-label">Purpose</div>
            <p className="mb-2">
              As advised and directed by the Country Director, the HR Sub-Committee is conducting
              a staff mapping and workforce planning exercise using the organisation’s current
              funding position as the baseline. The exercise will assess the full-time staff
              required by each department under the three scenarios discussed during the Strategic
              Reflection Workshop held in Hargeisa: Stress, Stable, and Growth.
            </p>
            <p className="mb-0">
              This exercise is role-based, evidence based, budget informed, and aligned with
              organisational needs. It assesses workforce requirements, not individual staff
              performance or employment decisions.
            </p>
          </div>

          <div className="wfp-instructions-section">
            <div className="wfp-instructions-label">Scenario Definitions</div>
            <div className="wfp-scenario-cards">
              <div className="wfp-scenario-card wfp-scenario-card--stress">
                <div className="wfp-scenario-card__title">Stress / Minimum</div>
                <p className="mb-0">
                  Bare minimum staff required to maintain essential operations during constrained
                  funding or reduced portfolio, based on an active portfolio below USD 8 million.
                </p>
              </div>
              <div className="wfp-scenario-card wfp-scenario-card--stable">
                <div className="wfp-scenario-card__title">Stable</div>
                <p className="mb-0">
                  Staff required for normal efficient operations under confirmed funding, workload,
                  programme targets, and donor obligations, based on an active and approved
                  portfolio of USD 8 million to USD 12 million.
                </p>
              </div>
              <div className="wfp-scenario-card wfp-scenario-card--growth">
                <div className="wfp-scenario-card__title">Growth</div>
                <p className="mb-0">
                  Additional staff required for approved expansion, additional fund, new locations,
                  increased targets, or scale-up, based on an active and approved portfolio above
                  USD 12 million. Growth roles are conditional on confirmed funding and management
                  approval.
                </p>
              </div>
            </div>
          </div>

          <div className="wfp-instructions-section">
            <div className="wfp-instructions-label">Completion Rules</div>
            <ul className="wfp-instructions-list mb-0">
              <li>Complete your department only.</li>
              <li>List roles, not staff names.</li>
              <li>Use whole numbers only.</li>
              <li>
                Provide evidence, risk score, budget details, and indicate whether each role can
                be merged, shared, reduced, or temporarily covered.
              </li>
            </ul>
          </div>

          <div className="wfp-instructions-section">
            <div className="wfp-instructions-label">Risk Scoring</div>
            <ul className="wfp-instructions-list mb-0">
              <li>
                <strong>1 = Low risk:</strong> Duties can be absorbed or temporarily covered with
                limited disruption.
              </li>
              <li>
                <strong>2 = Moderate risk:</strong> Absence may affect workload, quality,
                reporting, coordination, compliance, or operational efficiency.
              </li>
              <li>
                <strong>3 = High/Critical risk:</strong> Absence may significantly affect
                programme delivery, donor commitments, financial control, legal risk/HR
                compliance, operational continuity, or organisational reputation.
              </li>
            </ul>
          </div>

          <div className="wfp-instructions-section">
            <div className="wfp-instructions-label">Evidence Standard</div>
            <p className="mb-0">
              Each role must be supported by clear evidence showing the actual main
              responsibilities, workload, deliverables, funding, and operational risks linked to
              the role.
            </p>
          </div>

          <div className="wfp-instructions-section">
            <div className="wfp-instructions-label">Review and Approval</div>
            <p className="mb-2">
              HoD recommendations are not final decisions. The HR Sub-Committee will review
              submissions based on evidence, workload, risk, budget availability, role
              criticality, organisational needs, and the overall workforce assessment. The
              Committee will make recommendations for management review and approval.
            </p>
            <p className="mb-0">
              Unsupported, unclear, or unfunded requests may be returned for revision or approved
              only conditionally.
            </p>
          </div>

          <div className="wfp-instructions-section wfp-instructions-one-time">
            <p className="mb-0">
              Focus carefully while entering data: this form is one-time only per role and cannot
              be edited after submission.
            </p>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={confirmCreateInstructions}>
            Ok
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={statusModalOpen} toggle={closeStatusModal} centered>
        <ModalHeader toggle={closeStatusModal}>Update status</ModalHeader>
        <ModalBody>
          <p className="text-muted mb-3">
            {statusTarget?.role?.role_name || "Role"} · {statusTarget?.office || "—"}
          </p>
          <FormGroup>
            <Label for="wfp-status-select">Status</Label>
            <Input
              id="wfp-status-select"
              type="select"
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              disabled={savingStatus}
            >
              <option value="">Select status…</option>
              {WORKFORCE_STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={String(opt.value)}>
                  {opt.label} ({opt.value})
                </option>
              ))}
            </Input>
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={closeStatusModal} disabled={savingStatus}>
            Cancel
          </Button>
          <Button color="primary" onClick={saveStatus} disabled={savingStatus || !selectedStatus}>
            {savingStatus ? <Spinner size="sm" /> : "Save status"}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default WorkforcePlanningList;
